import {useCallback, useEffect, useState} from "react";
import {getBatchStatusApi} from "../../api/enrichmentApi";
import {statusPollRetryDelay} from "./batchStatusPolling";

const TERMINAL = new Set(["completed", "failed", "cancelled"]);

export function useSavedSearchBatchRecovery({resourceId, batchIds, setEnriching, setBatchStatus, refreshDetail}) {
  const idsKey = JSON.stringify([...new Set(batchIds || [])].filter(Boolean));
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const retry = useCallback(() => setRetryCount((count) => count + 1), []);

  useEffect(() => {
    const ids = JSON.parse(idsKey);
    setError(null);
    if (!resourceId || !ids.length) {
      setEnriching(false);
      return undefined;
    }
    let cancelled = false;
    let timer;
    let failures = 0;
    let completed = 0;
    let wasActive = false;
    let watchedIds = ids;
    let initialized = false;
    setEnriching(true);

    const poll = async () => {
      const responses = await Promise.all(watchedIds.map(async (batchId) => {
        try { return await getBatchStatusApi(batchId); }
        catch (failure) { return failure.response || {}; }
      }));
      if (cancelled) return;
      const failedIndex = responses.findIndex((response) => response?.status !== 200 || !response.data?.status);
      if (failedIndex >= 0) {
        const delay = statusPollRetryDelay(responses[failedIndex]?.status, failures++);
        if (delay !== null) {
          timer = setTimeout(poll, delay);
        } else {
          setError("We couldn’t recover enrichment status. Check again before starting another enrichment.");
        }
        return;
      }
      failures = 0;
      let statuses = responses.map((response) => response.data);
      if (!initialized) {
        watchedIds = watchedIds.filter((_id, index) => !TERMINAL.has(statuses[index].status));
        statuses = statuses.filter((status) => !TERMINAL.has(status.status));
        initialized = true;
        if (!statuses.length) {
          await refreshDetail();
          if (cancelled) return;
          setEnriching(false);
          return;
        }
      }
      const active = statuses.some((status) => !TERMINAL.has(status.status));
      const nextCompleted = statuses.reduce((sum, status) => sum + (Number(status.completed) || 0), 0);
      if (active || wasActive) {
        setBatchStatus({
          status: active ? "running" : statuses.some((status) => status.status === "failed") ? "failed"
            : statuses.some((status) => status.status === "cancelled") ? "cancelled" : "completed",
          total: statuses.reduce((sum, status) => sum + (Number(status.total) || 0), 0),
          completed: nextCompleted,
          failed: statuses.reduce((sum, status) => sum + (Number(status.failed) || 0), 0),
        });
      }
      const refresh = nextCompleted > completed || (wasActive && !active);
      completed = nextCompleted;
      wasActive = active;
      if (refresh) await refreshDetail();
      if (cancelled) return;
      if (active) timer = setTimeout(poll, 3000);
      else setEnriching(false);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [resourceId, idsKey, retryCount, refreshDetail, setEnriching, setBatchStatus]);

  return {error, retry};
}
